<template>
  <el-card>
    <div slot="header" class="toolbar">
      <span>商品信息</span>
      <div>
        <el-button v-if="$hasPermission('mall:goods:save')" type="primary" size="mini" @click="openCreate">新增商品</el-button>
      </div>
    </div>

    <div class="search-bar search-wrap">
      <el-input
        v-model.trim="query.goodsName"
        placeholder="请输入商品名称"
        clearable
        style="width: 220px"
        @keyup.enter.native="handleSearch"
      />
      <el-select v-model="query.categoryId" clearable placeholder="商品分类" style="width: 180px">
        <el-option v-for="item in categoryOptions" :key="item.id" :label="categoryOptionLabel(item)" :value="item.id" />
      </el-select>
      <el-select v-model="query.status" clearable placeholder="状态" style="width: 140px">
        <el-option :value="1" label="上架" />
        <el-option :value="0" label="下架" />
      </el-select>
      <el-button type="primary" @click="handleSearch">查询</el-button>
      <el-button @click="reset">重置</el-button>
    </div>

    <el-table :data="list" border style="width: 100%">
      <el-table-column prop="id" label="编号" width="90" />
      <el-table-column label="主图" width="110">
        <template slot-scope="{ row }">
          <el-image
            :src="resolveFileUrl(row.coverImageUrl)"
            :preview-src-list="row.coverImageUrl ? [resolveFileUrl(row.coverImageUrl)] : []"
            fit="cover"
            class="table-image"
          >
            <div slot="error" class="table-image-error">无图</div>
          </el-image>
        </template>
      </el-table-column>
      <el-table-column prop="goodsName" label="商品名称" min-width="180" />
      <el-table-column prop="goodsNo" label="商品编码" width="150" />
      <el-table-column prop="categoryName" label="分类" width="140" />
      <el-table-column prop="goodsType" label="类型" width="100">
        <template slot-scope="{ row }">
          {{ row.goodsType === 2 ? '虚拟商品' : '实物商品' }}
        </template>
      </el-table-column>
      <el-table-column prop="specEnabled" label="规格模式" width="100">
        <template slot-scope="{ row }">
          {{ row.specEnabled === 1 ? '多规格' : '单规格' }}
        </template>
      </el-table-column>
      <el-table-column prop="skuCount" label="SKU数" width="90" />
      <el-table-column prop="pointsPrice" label="积分" width="90" />
      <el-table-column prop="stock" label="库存" width="90" />
      <el-table-column prop="status" label="状态" width="90">
        <template slot-scope="{ row }">
          {{ row.status === 1 ? '上架' : '下架' }}
        </template>
      </el-table-column>
      <el-table-column prop="specSummary" label="规格概览" min-width="220" show-overflow-tooltip />
      <el-table-column label="操作" width="260" fixed="right">
        <template slot-scope="{ row }">
          <el-button v-if="$hasPermission('mall:goods:update')" size="mini" @click="openEdit(row)">编辑</el-button>
          <el-button
            v-if="$hasPermission('mall:goods:update')"
            :type="row.status === 1 ? 'warning' : 'success'"
            size="mini"
            @click="toggleStatus(row)"
          >
            {{ row.status === 1 ? '下架' : '上架' }}
          </el-button>
          <el-button v-if="$hasPermission('mall:goods:delete')" size="mini" type="danger" @click="remove(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-bar">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="query.page"
        :page-size="query.limit"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
      />
    </div>

    <el-dialog :title="form.id ? '编辑商品' : '新增商品'" :visible.sync="dialogVisible" width="1120px" :close-on-click-modal="false">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="110px">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="商品分类" prop="categoryId">
              <el-cascader
                v-model="form.categoryId"
                :options="categoryCascaderOptions"
                :props="categoryCascaderProps"
                clearable
                filterable
                style="width: 100%"
                placeholder="请选择具体的末级分类"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="商品编码" prop="goodsNo">
              <el-input v-model.trim="form.goodsNo" @input="handleGoodsNoInput" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="商品名称" prop="goodsName">
              <el-input v-model.trim="form.goodsName" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="商品类型" prop="goodsType">
              <el-select v-model="form.goodsType" style="width: 100%" @change="handleGoodsTypeChange">
                <el-option :value="1" label="实物商品" />
                <el-option :value="2" label="虚拟商品" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="规格模式">
              <el-radio-group v-model="form.specEnabled" @change="handleSpecEnabledChange">
                <el-radio :label="0">单规格</el-radio>
                <el-radio :label="1">多规格</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="是否物流" prop="needShipping">
              <el-select v-model="form.needShipping" style="width: 100%" disabled>
                <el-option :value="1" label="需要物流" />
                <el-option :value="0" label="无需物流" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="每人限购">
              <el-input-number v-model="form.limitPerUser" :min="0" :precision="0" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="限购时间(月)">
              <el-input-number v-model="form.limitPeriodMonths" :min="0" :precision="0" style="width: 100%" />
              <div class="form-tip">填0表示按历史总量永久累计限购</div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="库存预警">
              <el-switch
                v-model="form.stockWarnEnabled"
                :active-value="1"
                :inactive-value="0"
                active-text="开启"
                inactive-text="关闭"
              />
            </el-form-item>
          </el-col>
          <el-col v-if="form.stockWarnEnabled === 1" :span="12">
            <el-form-item label="预警值">
              <el-input-number v-model="form.stockWarnValue" :min="0" :precision="0" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item v-if="form.stockWarnEnabled === 1" label="预警邮箱">
          <el-select
            v-model="form.stockWarnEmails"
            multiple
            filterable
            placeholder="请选择预警接收邮箱"
            style="width: 100%"
          >
            <el-option v-for="email in stockWarnEmailOptions" :key="email" :label="email" :value="email" />
          </el-select>
        </el-form-item>

        <el-form-item label="商品主图" prop="coverImageUrl">
          <image-selector
            v-model="form.coverImageUrl"
            :category-code.sync="form.coverImageCategoryCode"
            :allowed-categories="['goods-cover']"
          />
        </el-form-item>

        <el-form-item label="商品轮播图">
          <image-selector
            v-model="form.bannerImageUrls"
            :category-code.sync="form.bannerImageCategoryCode"
            :allowed-categories="['goods-banner']"
            multiple
          />
        </el-form-item>

        <el-form-item label="商品详情图">
          <image-selector
            v-model="form.detailImageUrls"
            :category-code.sync="form.detailImageCategoryCode"
            :allowed-categories="['goods-detail']"
            multiple
          />
        </el-form-item>

        <div v-if="form.specEnabled === 1" class="spec-builder">
          <div class="sku-section-header">
            <span>规格项配置</span>
            <div class="spec-builder-actions">
              <el-button size="mini" @click="addSpecGroup">新增规格项</el-button>
              <el-button size="mini" type="primary" @click="generateSkuList">生成SKU组合</el-button>
            </div>
          </div>
          <div v-if="!form.specGroups.length" class="spec-empty">请先新增规格项，例如颜色、容量、尺码等。</div>
          <div v-for="(group, index) in form.specGroups" :key="index" class="spec-group-row">
            <el-input v-model.trim="group.name" placeholder="规格名，例如颜色 / 容量" class="spec-name-input" />
            <el-input
              v-model.trim="group.valuesText"
              placeholder="规格值用逗号分隔，例如 红色,蓝色,黑色"
              class="spec-values-input"
            />
            <el-button type="danger" plain size="mini" @click="removeSpecGroup(index)">删除</el-button>
          </div>
        </div>

        <div class="sku-section">
          <div class="sku-section-header">
            <span>SKU信息</span>
          </div>

          <el-table :data="form.skuList" border style="width: 100%">
            <el-table-column label="默认" width="70" align="center">
              <template slot-scope="{ $index }">
                <el-radio :value="resolveDefaultIndex()" :label="$index" @input="setDefaultSku($index)" />
              </template>
            </el-table-column>
            <el-table-column min-width="150">
              <template slot="header">
                <span class="required-label">SKU编码</span>
              </template>
              <template slot-scope="{ row }">
                <el-input v-model.trim="row.skuNo" />
              </template>
            </el-table-column>
            <el-table-column min-width="200">
              <template slot="header">
                <span class="required-label">规格描述</span>
              </template>
              <template slot-scope="{ row }">
                <el-input v-model.trim="row.specText" placeholder="例如 颜色:红色;容量:500ml" />
              </template>
            </el-table-column>
            <el-table-column label="SKU名称" min-width="160">
              <template slot-scope="{ row }">
                <el-input v-model.trim="row.skuName" placeholder="可不填" />
              </template>
            </el-table-column>
            <el-table-column width="100">
              <template slot="header">
                <span class="required-label">积分</span>
              </template>
              <template slot-scope="{ row }">
                <el-input v-model.number="row.pointsPrice" />
              </template>
            </el-table-column>
            <el-table-column width="100">
              <template slot="header">
                <span class="required-label">库存</span>
              </template>
              <template slot-scope="{ row }">
                <el-input v-model.number="row.stock" />
              </template>
            </el-table-column>
            <el-table-column label="状态" width="100">
              <template slot-scope="{ row }">
                <el-select v-model="row.status" @change="handleSkuStatusChange(row)">
                  <el-option :value="1" label="启用" />
                  <el-option :value="0" label="禁用" />
                </el-select>
              </template>
            </el-table-column>
            <el-table-column width="90">
              <template slot="header">
                <span class="required-label">排序</span>
              </template>
              <template slot-scope="{ row }">
                <el-input v-model.number="row.sortNum" />
              </template>
            </el-table-column>
          </el-table>
        </div>

        <el-form-item label="兑换说明">
          <el-input v-model.trim="form.exchangeNotice" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item v-if="form.goodsType === 2" label="虚拟发放内容">
          <el-input v-model.trim="form.virtualContent" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button
          v-if="$hasPermission(form.id ? 'mall:goods:update' : 'mall:goods:save')"
          type="primary"
          @click="save"
        >
          保存
        </el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import http from '../../utils/http'
import { resolveFileUrl } from '../../utils/file'
import ImageSelector from '../../components/mall/ImageSelector.vue'

function buildSpecJsonFromText (specText) {
  if (!specText) {
    return ''
  }
  const list = specText.split(';').map(item => item.trim()).filter(Boolean).map(item => {
    const [name, value] = item.split(':')
    return {
      name: (name || '').trim(),
      value: (value || '').trim()
    }
  }).filter(item => item.name && item.value)
  return list.length ? JSON.stringify(list) : specText
}

function defaultSku (goodsNo = '') {
  return {
    id: null,
    skuNo: goodsNo ? `${goodsNo}_DEFAULT` : '',
    skuName: '',
    specText: '默认规格',
    specJson: '[{"name":"默认规格","value":"默认"}]',
    coverImageUrl: '',
    pointsPrice: 0,
    stock: 0,
    salesCount: 0,
    sortNum: 1,
    status: 1,
    isDefault: 1
  }
}

function defaultSpecGroup () {
  return {
    name: '',
    valuesText: ''
  }
}

function defaultForm () {
  return {
    id: null,
    categoryId: null,
    goodsNo: '',
    goodsName: '',
    goodsType: 1,
    needShipping: 1,
    specEnabled: 0,
    coverImageUrl: '',
    coverImageCategoryCode: 'goods-cover',
    bannerImageUrls: [],
    bannerImageCategoryCode: 'goods-banner',
    detailImageUrls: [],
    detailImageCategoryCode: 'goods-detail',
    stockWarnEnabled: 0,
    stockWarnValue: 0,
    stockWarnEmails: [],
    pointsPrice: 0,
    stock: 0,
    salesCount: 0,
    sortNum: 0,
    status: 1,
    limitPerUser: 0,
    limitPeriodMonths: 0,
    exchangeNotice: '',
    detailContent: '',
    virtualContent: '',
    specGroups: [],
    skuList: [defaultSku()]
  }
}

function defaultQuery () {
  return {
    page: 1,
    limit: 10,
    goodsName: '',
    categoryId: null,
    status: null
  }
}

export default {
  components: {
    ImageSelector
  },
  computed: {
    categoryCascaderOptions () {
      const options = this.categoryOptions || []
      const topCategories = options.filter(item => Number(item.parentId || 0) === 0)
      const topIdSet = new Set(topCategories.map(item => Number(item.id)))
      const groups = topCategories.map(parent => this.buildCategoryCascaderNode(parent, options))
      const orphanChildren = options.filter(item => {
        const parentId = Number(item.parentId || 0)
        return parentId > 0 && !topIdSet.has(parentId)
      })
      if (orphanChildren.length) {
        groups.push({
          id: 'orphan',
          categoryName: '未匹配上级分类',
          disabled: true,
          children: orphanChildren.map(item => this.buildCategoryCascaderNode(item, options))
        })
      }
      return groups
    }
  },
  data () {
    return {
      query: defaultQuery(),
      total: 0,
      list: [],
      categoryOptions: [],
      categoryCascaderProps: {
        value: 'id',
        label: 'categoryName',
        children: 'children',
        emitPath: false,
        expandTrigger: 'click'
      },
      stockWarnEmailOptions: [],
      dialogVisible: false,
      form: defaultForm(),
      rules: {
        categoryId: [{ required: true, message: '请选择商品分类', trigger: 'change' }],
        goodsNo: [{ required: true, message: '请输入商品编码', trigger: 'blur' }],
        goodsName: [{ required: true, message: '请输入商品名称', trigger: 'blur' }],
        goodsType: [{ required: true, message: '请选择商品类型', trigger: 'change' }],
        coverImageUrl: [{ required: true, message: '请选择商品主图', trigger: 'change' }]
      }
    }
  },
  created () {
    this.load()
    this.loadCategories()
    this.loadStockWarnEmailOptions()
  },
  methods: {
    resolveFileUrl,
    async load () {
      const { data } = await http.get('/mall/goods/list', { params: this.query })
      const pageData = data.data || {}
      this.list = pageData.list || []
      this.total = Number(pageData.total || 0)
    },
    async loadCategories () {
      const { data } = await http.get('/mall/category/options')
      this.categoryOptions = data.data || []
    },
    buildCategoryCascaderNode (category, options) {
      const children = (options || [])
        .filter(item => Number(item.parentId || 0) === Number(category.id))
        .map(item => this.buildCategoryCascaderNode(item, options))
      const node = {
        id: category.id,
        categoryName: category.categoryName || ''
      }
      if (children.length) {
        node.children = children
      }
      return node
    },
    categoryOptionLabel (item) {
      return this.categoryPlainLabel(item)
    },
    categoryPlainLabel (item) {
      if (!item) return ''
      return item.categoryName || ''
    },
    categoryHasChildren (categoryId) {
      return (this.categoryOptions || []).some(item => Number(item.parentId || 0) === Number(categoryId))
    },
    isLeafCategory (categoryId) {
      return !!categoryId && !this.categoryHasChildren(categoryId)
    },
    validateGoodsCategory () {
      if (!this.form.categoryId) {
        this.$message.warning('请选择商品分类')
        return false
      }
      if (!this.isLeafCategory(this.form.categoryId)) {
        this.$message.warning('当前分类下存在子分类，请选择具体的二级分类')
        return false
      }
      return true
    },
    async loadStockWarnEmailOptions () {
      const { data } = await http.get('/mall/goods/stock-warn-email/options')
      this.stockWarnEmailOptions = data.data || []
    },
    handleSearch () {
      this.query.page = 1
      this.load()
    },
    handlePageChange (page) {
      this.query.page = page
      this.load()
    },
    handleSizeChange (limit) {
      this.query.limit = limit
      this.query.page = 1
      this.load()
    },
    reset () {
      this.query = defaultQuery()
      this.load()
    },
    openCreate () {
      this.form = defaultForm()
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate()
      })
    },
    async openEdit (row) {
      const { data } = await http.get(`/mall/goods/info/${row.id}`)
      const info = data.data || {}
      const skuList = (info.skuList || []).length ? (info.skuList || []) : [defaultSku(info.goodsNo || '')]
      this.form = {
        id: info.id,
        categoryId: info.categoryId,
        goodsNo: info.goodsNo || '',
        goodsName: info.goodsName || '',
        goodsType: info.goodsType,
        needShipping: Number(info.goodsType) === 2 ? 0 : 1,
        specEnabled: Number(info.specEnabled || 0),
        coverImageUrl: info.coverImageUrl || '',
        coverImageCategoryCode: 'goods-cover',
        bannerImageUrls: info.bannerImageUrls || [],
        bannerImageCategoryCode: 'goods-banner',
        detailImageUrls: info.detailImageUrls || [],
        detailImageCategoryCode: 'goods-detail',
        stockWarnEnabled: Number(info.stockWarnEnabled || 0),
        stockWarnValue: Number(info.stockWarnValue || 0),
        stockWarnEmails: info.stockWarnEmails || [],
        pointsPrice: info.pointsPrice || 0,
        stock: info.stock || 0,
        salesCount: info.salesCount || 0,
        sortNum: info.sortNum || 0,
        status: info.status,
        limitPerUser: info.limitPerUser || 0,
        limitPeriodMonths: info.limitPeriodMonths || 0,
        exchangeNotice: info.exchangeNotice || '',
        detailContent: info.detailContent || '',
        virtualContent: info.virtualContent || '',
        specGroups: Number(info.specEnabled || 0) === 1 ? this.parseSpecGroupsFromSkuList(skuList) : [],
        skuList
      }
      if (this.form.specEnabled === 0 && this.form.skuList.length) {
        const first = this.form.skuList[0]
        this.form.pointsPrice = first.pointsPrice || 0
        this.form.stock = first.stock || 0
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate()
      })
    },
    handleGoodsNoInput () {
      if (this.form.specEnabled === 0 && this.form.skuList.length) {
        this.form.skuList[0].skuNo = this.form.goodsNo ? `${this.form.goodsNo}_DEFAULT` : ''
      }
    },
    handleGoodsTypeChange (value) {
      this.form.needShipping = value === 2 ? 0 : 1
    },
    handleSpecEnabledChange (value) {
      if (value === 0) {
        this.form.specGroups = []
        this.form.skuList = [this.buildSingleSku()]
      } else {
        if (!this.form.specGroups.length) {
          this.form.specGroups = [defaultSpecGroup()]
        }
        if (!this.form.skuList.length) {
          this.form.skuList = [defaultSku(this.form.goodsNo)]
        }
      }
    },
    buildSingleSku () {
      const sku = defaultSku(this.form.goodsNo)
      sku.skuName = `${this.form.goodsName || ''} 默认规格`.trim()
      sku.coverImageUrl = this.form.coverImageUrl
      sku.pointsPrice = Number(this.form.pointsPrice || 0)
      sku.stock = Number(this.form.stock || 0)
      sku.salesCount = Number(this.form.salesCount || 0)
      sku.status = this.form.status
      const current = (this.form.skuList && this.form.skuList[0]) || {}
      Object.assign(sku, {
        ...current,
        skuNo: current.skuNo || sku.skuNo,
        skuName: current.skuName || sku.skuName,
        specText: current.specText || sku.specText,
        specJson: current.specJson || sku.specJson,
        coverImageUrl: current.coverImageUrl || sku.coverImageUrl,
        pointsPrice: Number(current.pointsPrice || 0),
        stock: Number(current.stock || 0),
        salesCount: Number(current.salesCount || 0),
        sortNum: Number(current.sortNum || 1),
        status: Number(current.status == null ? this.form.status : current.status),
        isDefault: 1
      })
      return sku
    },
    addSpecGroup () {
      this.form.specGroups.push(defaultSpecGroup())
    },
    removeSpecGroup (index) {
      this.form.specGroups.splice(index, 1)
      this.syncSkuListWithSpecGroups()
    },
    normalizeSpecGroups () {
      return (this.form.specGroups || []).map(item => ({
        name: (item.name || '').trim(),
        values: (item.valuesText || '')
          .split(/[,，\n\r]+/)
          .map(value => value.trim())
          .filter(Boolean)
      })).filter(item => item.name && item.values.length)
    },
    validateSpecGroups () {
      const nameSet = new Set()
      for (const item of this.form.specGroups || []) {
        const name = (item.name || '').trim()
        if (!name) {
          continue
        }
        if (nameSet.has(name)) {
          this.$message.warning('规格项配置中不允许有重复项')
          return false
        }
        nameSet.add(name)

        const valueSet = new Set()
        const values = (item.valuesText || '')
          .split(/[,，\n\r]+/)
          .map(value => value.trim())
          .filter(Boolean)
        for (const value of values) {
          if (valueSet.has(value)) {
            this.$message.warning(`规格项“${name}”下规格值不允许重复`)
            return false
          }
          valueSet.add(value)
        }
      }
      return true
    },
    cartesianProduct (groups) {
      if (!groups.length) {
        return []
      }
      let result = [[]]
      groups.forEach(group => {
        const temp = []
        result.forEach(prefix => {
          group.values.forEach(value => {
            temp.push(prefix.concat([{ name: group.name, value }]))
          })
        })
        result = temp
      })
      return result
    },
    generateSkuList () {
      if (!this.validateSpecGroups()) {
        return
      }
      const groups = this.normalizeSpecGroups()
      if (!groups.length) {
        this.$message.warning('请先维护规格项和规格值')
        return
      }
      if (!this.form.goodsNo) {
        this.$message.warning('请先填写商品编码')
        return
      }
      const combinations = this.cartesianProduct(groups)
      if (!combinations.length) {
        this.$message.warning('未生成有效的规格组合')
        return
      }
      const oldSkuList = this.form.skuList || []
      const oldMap = new Map(oldSkuList.map(item => [item.specText, item]))
      const reusableSingleSku = oldSkuList.length === 1 ? oldSkuList[0] : null
      this.form.skuList = combinations.map((combination, index) => {
        const specText = combination.map(item => `${item.name}:${item.value}`).join(';')
        const oldItem = oldMap.get(specText) || (index === 0 ? reusableSingleSku : null)
        return {
          id: oldItem ? oldItem.id : null,
          skuNo: oldItem && oldItem.skuNo ? oldItem.skuNo : `${this.form.goodsNo}_${index + 1}`,
          skuName: oldItem && oldItem.skuName ? oldItem.skuName : `${this.form.goodsName || ''} ${combination.map(item => item.value).join(' ')}`.trim(),
          specText,
          specJson: JSON.stringify(combination),
          coverImageUrl: oldItem && oldItem.coverImageUrl ? oldItem.coverImageUrl : this.form.coverImageUrl,
          pointsPrice: oldItem ? oldItem.pointsPrice : 0,
          stock: oldItem ? oldItem.stock : 0,
          salesCount: oldItem ? oldItem.salesCount : 0,
          sortNum: oldItem ? oldItem.sortNum : index + 1,
          status: oldItem ? oldItem.status : 1,
          isDefault: oldItem ? oldItem.isDefault : (index === 0 ? 1 : 0)
        }
      })
      if (!this.form.skuList.some(item => Number(item.isDefault) === 1) && this.form.skuList.length) {
        this.form.skuList[0].isDefault = 1
      }
      this.$message.success('SKU组合已生成')
    },
    syncSkuListWithSpecGroups () {
      if (this.form.specEnabled !== 1) {
        return
      }
      const groups = this.normalizeSpecGroups()
      if (!groups.length) {
        this.form.skuList = []
        return
      }
      const combinations = this.cartesianProduct(groups)
      if (!combinations.length) {
        this.form.skuList = []
        return
      }
      const oldSkuList = this.form.skuList || []
      const oldMap = new Map(oldSkuList.map(item => [item.specText, item]))
      const reusableSingleSku = oldSkuList.length === 1 ? oldSkuList[0] : null
      this.form.skuList = combinations.map((combination, index) => {
        const specText = combination.map(item => `${item.name}:${item.value}`).join(';')
        const oldItem = oldMap.get(specText) || (index === 0 ? reusableSingleSku : null)
        return {
          id: oldItem ? oldItem.id : null,
          skuNo: oldItem && oldItem.skuNo ? oldItem.skuNo : `${this.form.goodsNo || 'SKU'}_${index + 1}`,
          skuName: oldItem && oldItem.skuName ? oldItem.skuName : `${this.form.goodsName || ''} ${combination.map(item => item.value).join(' ')}`.trim(),
          specText,
          specJson: JSON.stringify(combination),
          coverImageUrl: oldItem && oldItem.coverImageUrl ? oldItem.coverImageUrl : this.form.coverImageUrl,
          pointsPrice: oldItem ? oldItem.pointsPrice : 0,
          stock: oldItem ? oldItem.stock : 0,
          salesCount: oldItem ? oldItem.salesCount : 0,
          sortNum: index + 1,
          status: oldItem ? oldItem.status : 1,
          isDefault: oldItem ? oldItem.isDefault : (index === 0 ? 1 : 0)
        }
      })
      if (!this.form.skuList.some(item => Number(item.isDefault) === 1) && this.form.skuList.length) {
        this.form.skuList[0].isDefault = 1
      }
    },
    parseSpecGroupsFromSkuList (skuList) {
      const groupMap = new Map()
      ;(skuList || []).forEach(sku => {
        const list = this.parseSpecList(sku.specJson || sku.specText)
        list.forEach(item => {
          if (!groupMap.has(item.name)) {
            groupMap.set(item.name, new Set())
          }
          groupMap.get(item.name).add(item.value)
        })
      })
      return Array.from(groupMap.entries()).map(([name, valueSet]) => ({
        name,
        valuesText: Array.from(valueSet).join(',')
      }))
    },
    parseSpecList (specSource) {
      if (!specSource) {
        return []
      }
      try {
        const json = JSON.parse(specSource)
        if (Array.isArray(json)) {
          return json.map(item => ({
            name: (item.name || '').trim(),
            value: (item.value || '').trim()
          })).filter(item => item.name && item.value)
        }
      } catch (e) {
        // ignore json parse error
      }
      return String(specSource).split(';').map(item => item.trim()).filter(Boolean).map(item => {
        const [name, value] = item.split(':')
        return {
          name: (name || '').trim(),
          value: (value || '').trim()
        }
      }).filter(item => item.name && item.value)
    },
    resolveDefaultIndex () {
      const index = this.form.skuList.findIndex(item => Number(item.isDefault) === 1)
      return index >= 0 ? index : 0
    },
    setDefaultSku (index) {
      this.form.skuList.forEach((item, itemIndex) => {
        item.isDefault = itemIndex === index ? 1 : 0
      })
    },
    handleSkuStatusChange (row) {
      const enabledCount = (this.form.skuList || []).filter(item => Number(item.status) === 1).length
      if (Number(row.status) === 0 && enabledCount === 0) {
        row.status = 1
        this.$message.warning('SKU至少保留一个启用状态，不允许全部禁用')
      }
    },
    validateSkuList () {
      if (!this.form.skuList.length) {
        this.$message.warning('请至少维护一个SKU')
        return false
      }
      if (this.form.specEnabled === 1 && !this.normalizeSpecGroups().length) {
        this.$message.warning('多规格商品请先维护规格项')
        return false
      }
      if (this.form.specEnabled === 1 && !this.validateSpecGroups()) {
        return false
      }
      const skuNoSet = new Set()
      const isBlank = value => value === undefined || value === null || value === ''
      let enabledCount = 0
      for (const item of this.form.skuList) {
        if (!item.skuNo) {
          this.$message.warning('SKU编码不能为空')
          return false
        }
        if (!item.specText) {
          this.$message.warning('规格描述不能为空')
          return false
        }
        if (isBlank(item.pointsPrice) || Number(item.pointsPrice) < 0) {
          this.$message.warning('SKU积分不能为空且不能小于0')
          return false
        }
        if (isBlank(item.stock) || Number(item.stock) < 0) {
          this.$message.warning('SKU库存不能为空且不能小于0')
          return false
        }
        if (isBlank(item.sortNum) || Number(item.sortNum) < 0) {
          this.$message.warning('SKU排序不能为空且不能小于0')
          return false
        }
        if (skuNoSet.has(item.skuNo)) {
          this.$message.warning('SKU编码不能重复')
          return false
        }
        skuNoSet.add(item.skuNo)
        if (Number(item.status) === 1) {
          enabledCount++
        }
      }
      if (enabledCount === 0) {
        this.$message.warning('SKU至少保留一个启用状态，不允许全部禁用')
        return false
      }
      return true
    },
    validateStockWarn () {
      if (Number(this.form.stockWarnEnabled || 0) !== 1) {
        return true
      }
      const isBlank = value => value === undefined || value === null || value === ''
      if (isBlank(this.form.stockWarnValue) || Number(this.form.stockWarnValue) < 0) {
        this.$message.warning('请输入正确的库存预警值')
        return false
      }
      const emails = this.normalizeStockWarnEmails(this.form.stockWarnEmails)
      if (!emails.length) {
        this.$message.warning('请至少选择一个库存预警接收邮箱')
        return false
      }
      const emailReg = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+$/
      const invalidEmail = emails.find(email => !emailReg.test(email))
      if (invalidEmail) {
        this.$message.warning(`库存预警邮箱格式不正确：${invalidEmail}`)
        return false
      }
      this.form.stockWarnEmails = emails
      return true
    },
    normalizeStockWarnEmails (emails) {
      return Array.from(new Set((emails || [])
        .map(email => String(email || '').trim().toLowerCase())
        .filter(Boolean)))
    },
    buildPayload () {
      let skuList = this.form.skuList.map(item => ({
        id: item.id,
        skuNo: item.skuNo,
        skuName: item.skuName,
        specText: item.specText,
        specJson: item.specJson || buildSpecJsonFromText(item.specText),
        coverImageUrl: item.coverImageUrl || this.form.coverImageUrl,
        pointsPrice: Number(item.pointsPrice || 0),
        stock: Number(item.stock || 0),
        salesCount: Number(item.salesCount || 0),
        sortNum: Number(item.sortNum || 0),
        status: Number(item.status || 0),
        isDefault: Number(item.isDefault || 0)
      }))

      if (this.form.specEnabled === 0) {
        skuList = [this.buildSingleSku()]
      }
      const defaultSkuItem = skuList.find(item => Number(item.isDefault) === 1) || skuList[0] || {}

      return {
        id: this.form.id,
        categoryId: this.form.categoryId,
        goodsNo: this.form.goodsNo,
        goodsName: this.form.goodsName,
        goodsType: this.form.goodsType,
        needShipping: Number(this.form.goodsType) === 2 ? 0 : 1,
        specEnabled: this.form.specEnabled,
        coverImageUrl: this.form.coverImageUrl,
        bannerImageUrls: this.form.bannerImageUrls,
        detailImageUrls: this.form.detailImageUrls,
        stockWarnEnabled: Number(this.form.stockWarnEnabled || 0),
        stockWarnValue: Number(this.form.stockWarnValue || 0),
        stockWarnEmails: Number(this.form.stockWarnEnabled || 0) === 1 ? this.normalizeStockWarnEmails(this.form.stockWarnEmails) : [],
        pointsPrice: Number(defaultSkuItem.pointsPrice || 0),
        stock: Number(this.form.specEnabled === 1
          ? skuList.reduce((total, item) => total + Number(item.stock || 0), 0)
          : defaultSkuItem.stock || 0),
        salesCount: Number(this.form.salesCount || 0),
        sortNum: Number(this.form.sortNum || 0),
        status: this.form.status,
        limitPerUser: Number(this.form.limitPerUser || 0),
        limitPeriodMonths: Number(this.form.limitPeriodMonths || 0),
        exchangeNotice: this.form.exchangeNotice,
        detailContent: this.form.detailContent,
        virtualContent: this.form.virtualContent,
        skuList
      }
    },
    toggleStatus (row) {
      const nextStatus = row.status === 1 ? 0 : 1
      const actionText = nextStatus === 1 ? '上架' : '下架'
      const message = nextStatus === 0
        ? `确定下架商品“${row.goodsName}”吗？下架后会同步禁用该商品在热门推荐信息中的推荐，小程序将不再展示该商品。`
        : `确定上架商品“${row.goodsName}”吗？上架商品不会自动恢复热门推荐，如需展示请到热门推荐信息中启用。`
      this.$confirm(message, '提示', {
        type: 'warning',
        confirmButtonText: actionText,
        cancelButtonText: '取消'
      }).then(async () => {
        const { data } = await http.post('/mall/goods/status', {
          id: row.id,
          status: nextStatus
        })
        if (data.code === 0) {
          this.$message.success(nextStatus === 0 ? '商品及热门推荐已同步下架' : '商品上架成功')
          this.load()
        }
      })
    },
    save () {
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        if (!this.validateGoodsCategory()) return
        if (!this.validateSkuList()) return
        if (!this.validateStockWarn()) return
        const payload = this.buildPayload()
        const url = payload.id ? '/mall/goods/update' : '/mall/goods/save'
        const { data } = await http.post(url, payload)
        if (data.code === 0) {
          this.$message.success(payload.id ? '商品修改成功' : '商品新增成功')
          this.dialogVisible = false
          this.load()
        }
      })
    },
    remove (row) {
      this.$confirm(`确定删除商品“${row.goodsName}”吗？`, '提示', { type: 'warning' }).then(async () => {
        const { data } = await http.post('/mall/goods/delete', [row.id])
        if (data.code === 0) {
          this.$message.success('商品删除成功')
          this.load()
        }
      })
    }
  }
}
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.search-wrap {
  flex-wrap: wrap;
}

.table-image {
  width: 64px;
  height: 64px;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  cursor: pointer;
}

.table-image-error {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  background: #f5f7fa;
}

.form-tip {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}

.pagination-bar {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.spec-builder {
  margin: 12px 0 18px;
  padding: 14px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fafcff;
}

.spec-builder-actions {
  display: flex;
  gap: 8px;
}

.spec-empty {
  padding: 10px 0 4px;
  color: #909399;
}

.spec-group-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.spec-name-input {
  width: 220px;
}

.spec-values-input {
  flex: 1;
}

.sku-section {
  margin: 18px 0;
}

.required-label::before {
  content: '*';
  margin-right: 4px;
  color: #f56c6c;
}

.sku-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  font-weight: 600;
  color: #303133;
}
</style>
